import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ICheckedTableItems,
  IFilterTripsOverview,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilterForRequest,
  ITripsOverviewInfo,
  ITripsOverviewTableData,
} from '../interfaces';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  filterForRequestAtom,
  hasMoreAtom,
  isActiveAdvanceFilterAtom,
  offsetAtom,
  rideBookDataAtom,
  rideItemAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import { convertTimeToSeconds, handleFilterKey, removeUsage } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface ITagProps {
  // item: IInfoParkingTag;
  rideId: string;
  customerId: number;
  id: number;
  name?: string;
  color: string;
  isParking?: boolean;
  isModal?: boolean;
  isRemovable: boolean;
}

function Tag(props: ITagProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const setRideItem = useSetRecoilState(rideItemAtom(String(props.rideId)));
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const resetState = () => {
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  };

  const removePurpose = async () => {
    trackEvent(['ride book', 'click', 'Ride book remove purpose']);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };
    const res = await removeUsage(
      [String(props.id)],
      props.customerId,
      props.rideId,
      filter,
      props.isParking
    );
    if (res) {
      if (isActiveAdvanceFilter.ridebookPurpose) {
        resetState();
      } else {
        setRideItem((oldState: ITripsOverViewRecord | null) => {
          if (oldState) {
            const newState = { ...oldState };
            const infoData: ITripsOverviewInfo = { ...oldState.info };
            if (props.isParking) {
              infoData.parkingTags = infoData.parkingTags.filter(
                (i) => String(i.id) !== String(props.id)
              );
            } else {
              infoData.rideTags = infoData.rideTags.filter(
                (i) => String(i.id) !== String(props.id)
              );
            }
            if (props.isParking && oldState.parking) {
              const parkingData = { ...oldState.parking };
              parkingData.summary = {
                ...parkingData.summary,
                tag: Number(parkingData.summary?.tag) - 1,
              };
              newState.parking = { ...parkingData };
            } else {
              infoData.summary = { ...infoData.summary, tag: Number(infoData.summary.tag) - 1 };
            }
            newState.info = { ...infoData };
            return newState;
          }
          return null;
        });
      }
    }
  };

  const removeElement = () => {
    let element: JSX.Element | null = null;
    if (props.isRemovable) {
      element = (
        <a
          href="#"
          title={f({ id: 'tripsOverview.modal.removePurpose' })}
          className="text-gray-lighten cmd-icons cmd-icon-bin mx-1"
          onClick={removePurpose}
          data-cy="tripsOverview-modal-removePurpose"
        >
          <span className="sr-only">{f({ id: 'tripsOverview.modal.removePurpose' })}</span>
        </a>
      );
    }
    return element;
  };

  return (
    <div
      className={
        props.isModal
          ? 'row no-gutters flex-nowrap pl-2 py-1'
          : 'row no-gutters flex-nowrap py-1 pr-0 w-100'
      }
    >
      <div className="col-auto text-c-orange-dark pt-1" key={props.rideId}>
        <i
          className="cmd-icons cmd-icon-single-folded-content fs-4 text-center"
          style={{ display: 'inline-block', width: '16px' }}
        />
      </div>
      <div className="col pl-2">
        <div className="b-edit-input-field m-0">
          <div>
            {String(props.name)}
            <span
              className="e-badge-sq"
              style={{
                backgroundColor: props.color,
              }}
            />
            <div className="b-edit-input-field-actions" key="tagBefore">
              {removeElement()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tag;
